/**
 * Override Service
 *
 * Pure functions for applying and managing overrides for taxable values and millage rates.
 * This service provides the core logic for the override system without side effects.
 *
 * @see types/overrides.ts for type definitions
 */

import type {
  ConfigurationOverrides,
  CustomizationMetadata,
  MillageOverrides,
  MillageRateOverride,
  OverrideType,
  TaxableValueGrowthRateOverride,
  TaxableValueOverrides,
} from '~/types/overrides';

// ============================================================================
// Application Functions - Apply overrides to base data
// ============================================================================

// NOTE: Taxable value growth rate overrides are now applied directly in TaxableValueBuilder
// via the projectTaxableValues function. The applyTaxableValueOverrides function has been
// removed as it worked with the old absolute value override approach.

/**
 * Apply millage rate overrides to a base millage map
 *
 * Returns a new map with overrides merged in. Original map is not mutated.
 * Override rates completely replace shape-generated rates for their respective years.
 *
 * @param baseMap - The millage map from shape generation (year -> rate)
 * @param overrides - Optional overrides to apply
 * @returns New millage map with overrides applied
 */
export function applyMillageOverrides(
  baseMap: Record<number, number>,
  overrides: MillageOverrides | null | undefined
): Record<number, number> {
  if (!overrides || Object.keys(overrides.byYear).length === 0) {
    return baseMap;
  }

  const result: Record<number, number> = {};

  for (const [yearStr, rate] of Object.entries(baseMap)) {
    const year = Number.parseInt(yearStr, 10);
    const override = overrides.byYear[year];

    if (override) {
      // Validate override rate - use base rate if invalid
      if (!Number.isFinite(override.overrideRate) || override.overrideRate <= 0) {
        console.warn(
          `[applyMillageOverrides] Invalid override rate for year ${year}:`,
          override.overrideRate
        );
        result[year] = rate;
      } else {
        result[year] = override.overrideRate;
      }
    } else {
      result[year] = rate;
    }
  }

  // Warn if base map was empty (critical issue)
  if (Object.keys(baseMap).length === 0) {
    console.error('[applyMillageOverrides] Base map is empty!', {
      sourceShapeId: overrides.sourceShapeId,
    });
  }

  return result;
}

// ============================================================================
// Management Functions - Create and modify override collections
// ============================================================================

/**
 * Set or update a taxable value growth rate override for a specific year
 *
 * When a year's growth rate is overridden, the TaxableValueBuilder will use
 * the override rate instead of the default, creating a cascading effect on
 * all subsequent year values.
 *
 * @param current - Current overrides (or null if none exist)
 * @param taxYear - The year to override
 * @param originalGrowthRate - The original growth rate (as decimal, e.g., 0.035 for 3.5%)
 * @param overrideGrowthRate - The new growth rate to use (as decimal)
 * @param reason - Optional reason for the override
 * @returns New TaxableValueOverrides with the change applied
 */
export function setTaxableValueGrowthRateOverride(
  current: TaxableValueOverrides | null,
  taxYear: number,
  originalGrowthRate: number,
  overrideGrowthRate: number,
  reason?: string
): TaxableValueOverrides {
  const existingByYear = current?.byYear ?? {};

  const newOverride: TaxableValueGrowthRateOverride = {
    taxYear,
    originalGrowthRate,
    overrideGrowthRate,
    reason,
  };

  return {
    byYear: {
      ...existingByYear,
      [taxYear]: newOverride,
    },
  };
}

/**
 * Remove a taxable value override for a specific year
 *
 * @param current - Current overrides
 * @param taxYear - The year to remove override for
 * @returns Updated overrides, or null if no overrides remain
 */
export function removeTaxableValueOverride(
  current: TaxableValueOverrides,
  taxYear: number
): TaxableValueOverrides | null {
  const { [taxYear]: _removed, ...remaining } = current.byYear;

  if (Object.keys(remaining).length === 0) {
    return null;
  }

  return { byYear: remaining };
}

/**
 * Clear all taxable value overrides
 *
 * @returns null (no overrides)
 */
export function clearTaxableValueOverrides(): null {
  return null;
}

/**
 * Set or update a millage rate override for a specific year
 *
 * @param current - Current overrides (must have baseMillageMap set)
 * @param taxYear - The year to override
 * @param originalRate - The original shape-generated rate
 * @param overrideRate - The new rate to use
 * @param reason - Optional reason for the override
 * @returns New MillageOverrides with the change applied
 */
export function setMillageOverride(
  current: MillageOverrides,
  taxYear: number,
  originalRate: number,
  overrideRate: number,
  reason?: string
): MillageOverrides {
  const existingByYear = current.byYear ?? {};

  const newOverride: MillageRateOverride = {
    taxYear,
    originalValue: originalRate,
    overrideRate,
    reason,
  };

  return {
    byYear: {
      ...existingByYear,
      [taxYear]: newOverride,
    },
    baseMillageMap: current.baseMillageMap,
    sourceShapeId: current.sourceShapeId,
    sourceShapeName: current.sourceShapeName,
  };
}

/**
 * Remove a millage rate override for a specific year
 *
 * @param current - Current overrides
 * @param taxYear - The year to remove override for
 * @returns Updated overrides (always returns with baseMillageMap preserved)
 */
export function removeMillageOverride(
  current: MillageOverrides,
  taxYear: number
): MillageOverrides {
  const { [taxYear]: _removed, ...remaining } = current.byYear;

  return {
    byYear: remaining,
    baseMillageMap: current.baseMillageMap,
    sourceShapeId: current.sourceShapeId,
    sourceShapeName: current.sourceShapeName,
  };
}

/**
 * Clear all millage overrides
 *
 * @returns null (no overrides)
 */
export function clearMillageOverrides(): null {
  return null;
}

/**
 * Initialize millage overrides from a candidate's millage information
 *
 * Creates a MillageOverrides structure pre-populated with the base millage map
 * and source shape info. The baseMillageMap is the candidate's current millage
 * rates - any subsequent overrides will be applied on top of this map.
 *
 * @param baseMillageMap - The candidate's current millage map (year -> rate)
 * @param shapeId - The source shape ID (for display/reference)
 * @param shapeName - The source shape name for display
 * @returns MillageOverrides with base map and source info, ready for overrides
 */
export function initializeMillageOverrides(
  baseMillageMap: Record<number, number>,
  shapeId?: string,
  shapeName?: string
): MillageOverrides {
  return {
    byYear: {},
    baseMillageMap: { ...baseMillageMap },
    sourceShapeId: shapeId,
    sourceShapeName: shapeName,
  };
}

// ============================================================================
// Query Functions - Check override state
// ============================================================================

/**
 * Check if configuration has any taxable value overrides
 */
export function hasTaxableValueOverrides(
  overrides: ConfigurationOverrides | null | undefined
): boolean {
  if (!overrides?.taxableValue) return false;
  return Object.keys(overrides.taxableValue.byYear).length > 0;
}

/**
 * Check if configuration has any millage overrides
 */
export function hasMillageOverrides(overrides: ConfigurationOverrides | null | undefined): boolean {
  if (!overrides?.millage) return false;
  return Object.keys(overrides.millage.byYear).length > 0;
}

/**
 * Check if configuration has any overrides of any type
 */
export function hasAnyOverrides(overrides: ConfigurationOverrides | null | undefined): boolean {
  return hasTaxableValueOverrides(overrides) || hasMillageOverrides(overrides);
}

/**
 * Get total count of overridden values across all override types
 */
export function getOverrideCount(overrides: ConfigurationOverrides | null | undefined): number {
  let count = 0;

  if (overrides?.taxableValue) {
    count += Object.keys(overrides.taxableValue.byYear).length;
  }

  if (overrides?.millage) {
    count += Object.keys(overrides.millage.byYear).length;
  }

  return count;
}

/**
 * Get list of which override types are present
 */
export function getOverrideTypes(
  overrides: ConfigurationOverrides | null | undefined
): OverrideType[] {
  const types: OverrideType[] = [];

  if (hasTaxableValueOverrides(overrides)) {
    types.push('taxableValue');
  }

  if (hasMillageOverrides(overrides)) {
    types.push('millage');
  }

  return types;
}

/**
 * Build customization metadata for a configuration
 *
 * Used for UI display (badges, indicators) and tracking inheritance.
 *
 * @param overrides - The configuration's overrides
 * @param inheritedFrom - Optional source configuration ID if inherited
 * @returns CustomizationMetadata object
 */
export function buildCustomizationMetadata(
  overrides: ConfigurationOverrides | null | undefined,
  inheritedFrom?: string
): CustomizationMetadata {
  const overrideTypes = getOverrideTypes(overrides);

  return {
    hasOverrides: overrideTypes.length > 0,
    overrideTypes,
    inheritedFrom,
  };
}

// ============================================================================
// Merge Functions - For configuration cloning/inheritance
// ============================================================================

/**
 * Deep clone configuration overrides for inheritance
 *
 * Creates a complete copy of overrides that can be modified independently.
 *
 * @param source - Source overrides to clone
 * @returns New ConfigurationOverrides object, or null if source is null/empty
 */
export function cloneOverrides(
  source: ConfigurationOverrides | null | undefined
): ConfigurationOverrides | null {
  if (!source) return null;
  if (!hasAnyOverrides(source)) return null;

  const result: ConfigurationOverrides = {};

  if (source.taxableValue && Object.keys(source.taxableValue.byYear).length > 0) {
    result.taxableValue = {
      byYear: { ...source.taxableValue.byYear },
    };
  }

  if (source.millage && Object.keys(source.millage.byYear).length > 0) {
    result.millage = {
      byYear: { ...source.millage.byYear },
      baseMillageMap: { ...source.millage.baseMillageMap },
      sourceShapeId: source.millage.sourceShapeId,
      sourceShapeName: source.millage.sourceShapeName,
    };
  }

  return Object.keys(result).length > 0 ? result : null;
}

/**
 * Merge new overrides with existing ones
 *
 * New overrides take precedence over existing ones for the same years.
 *
 * @param existing - Existing overrides
 * @param incoming - New overrides to merge in
 * @returns Merged ConfigurationOverrides
 */
export function mergeOverrides(
  existing: ConfigurationOverrides | null | undefined,
  incoming: ConfigurationOverrides | null | undefined
): ConfigurationOverrides | null {
  if (!incoming) return cloneOverrides(existing);
  if (!existing) return cloneOverrides(incoming);

  const result: ConfigurationOverrides = {};

  // Merge taxable value overrides
  if (existing.taxableValue || incoming.taxableValue) {
    result.taxableValue = {
      byYear: {
        ...(existing.taxableValue?.byYear ?? {}),
        ...(incoming.taxableValue?.byYear ?? {}),
      },
    };
  }

  // Merge millage overrides (incoming takes precedence for source info and baseMillageMap)
  if (existing.millage || incoming.millage) {
    result.millage = {
      byYear: {
        ...(existing.millage?.byYear ?? {}),
        ...(incoming.millage?.byYear ?? {}),
      },
      baseMillageMap: incoming.millage?.baseMillageMap ?? existing.millage?.baseMillageMap ?? {},
      sourceShapeId: incoming.millage?.sourceShapeId ?? existing.millage?.sourceShapeId,
      sourceShapeName: incoming.millage?.sourceShapeName ?? existing.millage?.sourceShapeName,
    };
  }

  return hasAnyOverrides(result) ? result : null;
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validate a taxable value growth rate override
 *
 * @param overrideGrowthRate - The proposed growth rate (as decimal, e.g., 0.035 for 3.5%)
 * @returns Error message if invalid, null if valid
 */
export function validateTaxableValueGrowthRateOverride(overrideGrowthRate: number): string | null {
  if (!Number.isFinite(overrideGrowthRate)) {
    return 'Growth rate must be a valid number';
  }
  // Allow negative growth rates (contraction) but not extremely negative
  if (overrideGrowthRate < -0.5) {
    return 'Growth rate cannot be less than -50%';
  }
  // Cap at reasonable positive growth
  if (overrideGrowthRate > 0.5) {
    return 'Growth rate cannot exceed 50%';
  }
  return null;
}

/**
 * Validate a millage rate override
 *
 * @param overrideRate - The proposed override rate
 * @param peakMillage - Optional peak millage constraint
 * @returns Error message if invalid, null if valid
 */
export function validateMillageOverride(overrideRate: number, peakMillage?: number): string | null {
  if (overrideRate <= 0) {
    return 'Millage rate must be greater than zero';
  }
  if (!Number.isFinite(overrideRate)) {
    return 'Millage rate must be a valid number';
  }
  if (peakMillage !== undefined && overrideRate > peakMillage) {
    return `Millage rate cannot exceed peak millage of ${peakMillage}`;
  }
  return null;
}
